import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchApiWithToken, type WithHydraPagination } from '~/api/utils'
import type { Action } from '../types'

export type ActionsCollectionResponse = WithHydraPagination<Action>

export type UseActionsParams = {
  user?: number | number[]
  agency?: number | number[]
  types?: string[]
  contacts?: number[]
  applicants?: number[]
  createdBy?: number[]
  completedBy?: number[]
  linkedTo?: string[]
  scheduledDate?: {
    after?: string
    before?: string
  }
  itemsPerPage?: number
  isCompleted?: boolean | null
  /** Completed one way or another, or cancelled (`completed=treated`); overrides `isCompleted` and `isCancelled`. */
  treated?: boolean
  isCancelled?: boolean
  isPrivate?: boolean
  includePrivate?: boolean
  expectsCompletion?: boolean
  order?: 'eventDate' | 'scheduledDate' | 'completionDate'
  orderDirection?: 'asc' | 'desc'
  enabled?: boolean
}

export const buildActionsSearchParams = (
  params: UseActionsParams,
  page = 1
): URLSearchParams => {
  const searchParams = new URLSearchParams()

  searchParams.append('page', page.toString())
  if (params.itemsPerPage) {
    searchParams.append('itemsPerPage', params.itemsPerPage.toString())
  }

  if (params.user) {
    if (Array.isArray(params.user)) {
      searchParams.append('user', params.user.join(','))
    } else {
      searchParams.append('user', params.user.toString())
    }
  }
  if (params.agency) {
    searchParams.append(
      'agency',
      Array.isArray(params.agency)
        ? params.agency.join(',')
        : params.agency.toString()
    )
  }
  if (params.types && params.types.length > 0) {
    searchParams.append('type', params.types.join(','))
  }
  if (params.contacts && params.contacts.length > 0) {
    searchParams.append('contacts', params.contacts.join(','))
  }
  if (params.applicants && params.applicants.length > 0) {
    searchParams.append('applicants', params.applicants.join(','))
  }
  for (const id of params.createdBy ?? []) {
    searchParams.append('createdBy[]', id.toString())
  }
  for (const id of params.completedBy ?? []) {
    searchParams.append('completedBy[]', id.toString())
  }
  if (params.linkedTo && params.linkedTo.length > 0) {
    searchParams.append('linkedTo', params.linkedTo.join(','))
  }
  if (params.scheduledDate?.after) {
    searchParams.append('scheduledDate[after]', params.scheduledDate.after)
  }
  if (params.scheduledDate?.before) {
    searchParams.append('scheduledDate[before]', params.scheduledDate.before)
  }
  searchParams.append(
    `order[${params.order ?? 'eventDate'}]`,
    params.orderDirection ?? 'asc'
  )
  if (params.treated) {
    searchParams.append('completed', 'treated')
  } else {
    if (params.isCompleted !== null) {
      searchParams.append('completed', params.isCompleted ? 'true' : 'false')
    }
    searchParams.append('cancel', params.isCancelled ? 'true' : 'false')
  }
  if (params.includePrivate) {
    searchParams.append('private', 'true')
  }
  if (params.expectsCompletion !== undefined) {
    searchParams.append(
      'expectsCompletion',
      params.expectsCompletion ? 'true' : 'false'
    )
  }

  return searchParams
}

export const fetchActionsPage = (params: UseActionsParams, page = 1) =>
  fetchApiWithToken<ActionsCollectionResponse>({
    method: 'get',
    url: `/v1/actions?${buildActionsSearchParams(params, page).toString()}`,
    useLDJson: true
  })

export const getNextActionsPageParam = (
  lastPage: ActionsCollectionResponse
): number | undefined => {
  if (lastPage['hydra:view']?.['hydra:next']) {
    const url = new URL(
      lastPage['hydra:view']['hydra:next'],
      window.location.origin
    )
    return Number.parseInt(url.searchParams.get('page') || '1', 10)
  }
  return undefined
}

export const useActions = (params: UseActionsParams = {}) => {
  return useInfiniteQuery({
    queryKey: ['actions', params],
    queryFn: ({ pageParam = 1 }) => fetchActionsPage(params, pageParam),
    getNextPageParam: getNextActionsPageParam,
    initialPageParam: 1,
    staleTime: 300_000,
    enabled: params.enabled
  })
}
